<template>
  <model-form
    ref="modelForm"
    :model="movieTerm"
    :title="`${movieTerm.isLocal ? 'Add' : 'Edit'} ${movieTerm.format} (${movieTerm.id})`"
    submodel
    :show-and-close="false"
    @change="$emit('change', movieTerm)"
  >
    <div
      class="row q-col-gutter-md"
    >
      <div class="col-4 column q-col-gutter-y-sm items-start justify-start">
        <model-form-input
          field="abbreviation"
          class="full-width"
          label="Abbreviation"
          maxlength="5"
          bottom-slots
        />
        <div
          class="full-width"
        >
          <showing-badge-select
            :model="movieTerm"
            class="full-width"
            :disable="movieTerm.locked || isOnlyDefaultTerm"
            :rules="[validateMovieTerms]"
            @update:model-value="validateMovieTerms();"
          />
          <q-tooltip v-if="movieTerm.locked">
            Cannot update showing badges for movie terms with sent box office reports.
          </q-tooltip>
          <q-tooltip v-else-if="isOnlyDefaultTerm">
            Must have at least one default term.
          </q-tooltip>
        </div>
        <div class="row q-col-gutter-x-sm items-center full-width">
          <model-form-toggle
            field="hasFlatFee"
            class="col-6 q-pb-md"
            label="Has Flat Fee"
          />
          <model-form-input
            field="flatFee"
            class="col-6"
            label="Flat Fee"
            type="number"
            :prefix="currentCircuit.localeCurrencySymbol"
            step="0.25"
            :rules="[val => val !== null || 'Field is required']"
            :disable="!movieTerm.hasFlatFee"
          />
        </div>
        <model-form-input
          field="minimumGuarantee"
          class="full-width"
          type="number"
          label="Minimum Guarantee"
          :prefix="currentCircuit.localeCurrencySymbol"
          step="0.25"
          :rules="[val => val !== null || 'Field is required']"
        />
        <model-form-input
          field="deposit"
          class="full-width"
          type="number"
          label="Initial Deposit"
          :prefix="currentCircuit.localeCurrencySymbol"
          step="0.25"
          :rules="[val => val !== null || 'Field is required']"
        />
        <model-form-input
          field="notes"
          class="full-width"
          label="Notes"
          type="textarea"
          maxlength="5000"
        />
      </div>

      <div class="col-8 row">
        <q-table
          :key="`movieTerm-${movieTerm.id}`"
          v-model:pagination="pagination"
          class="col"
          :columns="movieTermWeeksColumns"
          :rows="movieTerm.sortedMovieTermWeeks"
          :rows-per-page-options="[0]"
          hide-pagination
          bordered
          flat
          wrap-cells
          row-key="id"
        >
          <template #top>
            <div class="q-table__title">
              Distribution Terms
            </div>

            <q-space />

            <q-btn
              class="q-mx-xs"
              label="Add Preview"
              outline
              :disable="movieTerm.hasFlatFee"
              @click="addMovieTermWeek('preview')"
            />
            <q-btn
              class="q-ml-xs"
              label="Add Week"
              outline
              :disable="movieTerm.hasFlatFee"
              @click="addMovieTermWeek('release')"
            />
          </template>

          <template #body="props">
            <q-tr
              v-if="props.row.weekNumber === 1 && props.row.type === 'release'"
              :key="`dateOfFirstShowing_${props.row.id}`"
              :props="props"
              class="bg-grey-2 q-virtual-scroll--with-prev"
            >
              <q-td colspan="100%">
                <div>
                  <span
                    class="text-bold"
                  >
                    Date of First Showing
                  </span>
                  <q-icon
                    name="info"
                    size="xs"
                    rounded
                  >
                    <q-tooltip>
                      Showings before this date will be included in Preview Week Box Office Reports (above).
                      <br>
                      Showings on and after this date will be included in Release Week Box Office Reports (below).
                    </q-tooltip>
                  </q-icon>
                  <formatted-date
                    :value="dateOfFirstShowing"
                    class="q-ml-lg"
                    component="chip"
                    format="date"
                  />
                </div>
              </q-td>
            </q-tr>
            <q-tr
              :key="props.row.id"
              :props="props"
            >
              <q-td
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
              >
                <template v-if="col.component === 'term'">
                  <q-input
                    :model-value="props.row.termPercentHundredths / 100.0"
                    type="number"
                    suffix=" %"
                    step="1"
                    min="0"
                    max="100"
                    :bg-color="props.row.locked ? 'grey-2' : null"
                    dense
                    hide-bottom-space
                    :disable="movieTerm.hasFlatFee"
                    :rules="[
                      val => val !== null || 'Field is required',
                      val => val <= 100 || 'Value must be less than or equal to 100',
                      val => val >= 0 || 'Value must be greater than or equal to 0',
                    ]"
                    @update:model-value="input => props.row.termPercentHundredths = Math.round(input * 100.0)"
                  >
                    <template #append>
                      <q-icon
                        v-if="props.row.locked"
                        name="lock"
                        color="grey-6"
                      >
                        <q-tooltip>
                          This week has already been used to send Box Office Reports.
                          <br>
                          Changes to this week may generate adjustments or require Box Office Reports be resent for accurate rental fees.
                        </q-tooltip>
                      </q-icon>
                      <q-icon
                        v-else
                        name="lock_open"
                        color="grey-6"
                      >
                        <q-tooltip>
                          This week has not been used to send Box Office Reports and can be modified freely.
                        </q-tooltip>
                      </q-icon>
                    </template>
                  </q-input>
                </template>
                <template v-else-if="col.component === 'actions'">
                  <div class="row q-gutter-sm justify-end no-wrap">
                    <slot
                      name="prepend-row-actions"
                      v-bind="props"
                    />
                    <q-btn
                      v-if="!props.row.locked && (props.row.type === 'preview' || props.row.weekNumber !== 1)"
                      icon="delete"
                      color="negative"
                      flat
                      dense
                      :disable="movieTerm.hasFlatFee || (props.row.type === 'release' && movieTerm.releaseMovieTermWeeks.length === 1)"
                      @click="deleteMovieTermWeek(props.row)"
                    />
                  </div>
                </template>
                <template v-else-if="col.component === 'name'">
                  {{ movieTermWeekName(props.row) }}
                </template>
                <template v-else>
                  {{ col.value }}
                </template>
              </q-td>
            </q-tr>
          </template>
        </q-table>
      </div>
    </div>
    <q-tabs
      v-model="tab"
      class="col text-grey q-mt-sm"
      active-color="primary"
      indicator-color="primary"
      inline-label
      align="justify"
    >
      <q-tab
        name="Box Office Reports"
        label="Box Office Reports"
      />
      <q-tab
        name="Box Office Settlements"
        label="Box Office Settlements"
      />
      <q-tab
        name="Showings"
        label="Showings"
      />
    </q-tabs>
    <q-tab-panels
      v-model="tab"
      class="col-12"
    >
      <q-tab-panel
        name="Box Office Reports"
      >
        <box-office-report-table
          class="col-12"
          :movie="movieTerm.movie"
          :movie-term="movieTerm"
          :initial-selection="adjustedBoxOfficeReports"
          :column-fn="processSubTableColumns"
        />
      </q-tab-panel>
      <q-tab-panel
        name="Box Office Settlements"
      >
        <box-office-report-settlement-table
          class="col-12"
          :movie="movieTerm.movie"
          :movie-term="movieTerm"
          :initial-selection="adjustedBoxOfficeReportSettlements"
          :column-fn="processSubTableColumns"
        />
      </q-tab-panel>
      <q-tab-panel
        name="Showings"
      >
        <showing-table
          class="col-12"
          :movie-term-id="movieTerm.id"
          initial-period="all"
          :column-fn="processSubTableColumns"
        />
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import BoxOfficeReportTable from 'src/modules/admin/box-office-report/BoxOfficeReportTable'
import FormattedDate from 'src/modules/admin/components/FormattedDate'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import BoxOfficeReportSettlementTable from 'src/modules/admin/settlement/BoxOfficeReportSettlementTable'
import ShowingTable from 'src/modules/admin/showing/ShowingTable'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect'

export default {
  name: 'MovieTermForm',
  components: {
    BoxOfficeReportTable,
    BoxOfficeReportSettlementTable,
    FormattedDate,
    ModelForm,
    ShowingBadgeSelect,
    ShowingTable
  },
  configuration: ['currentCircuit', 'currentSite'],
  props: {
    originalMovieTermsData: {
      type: Array,
      required: false,
      default: () => ([])
    }
  },
  emits: ['change'],
  models: {
    movieTerm: {
      movieTermWeeks: {
        boxOfficeReports: {
          fields: ['id'],
          boxOfficeReportSettlements: {
            fields: ['id']
          }
        },
        locked: true
      },
      movie: {
        fields: ['id', 'name', 'urlSlug', 'color'],
        movieTerms: {
          showingBadges: true
        }
      },
      showingBadges: true,
      notes: true,
      locked: true
    }
  },
  data () {
    return {
      tab: 'Box Office Reports',
      movieTermWeeksColumns: [
        { name: 'name', label: 'Name', field: 'name', align: 'left', component: 'name' },
        { name: 'percentage', label: 'Percentage', align: 'center', field: 'termPercentHundredths', component: 'term' },
        { name: 'actions', label: 'Actions', component: 'actions' }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  computed: {
    originalMovieTermWeeks () {
      const originalMovieTermData = _.find(this.originalMovieTermsData, { id: this.movieTerm.id })

      if (_.isNil(originalMovieTermData)) return []

      const originalMovieTermWeeks = {
        preview: {},
        release: {}
      }

      _(originalMovieTermData.movieTermWeeks).sortBy('weekNumber').each(movieTermWeek => {
        originalMovieTermWeeks[movieTermWeek.type][movieTermWeek.id] = movieTermWeek
      })

      return originalMovieTermWeeks
    },
    adjustedBoxOfficeReports () {
      const adjustedBoxOfficeReports = []
      _(this.movieTerm.movieTermWeeks.data).each(movieTermWeek => {
        if (movieTermWeek.isLocal) return

        if (movieTermWeek.isDirty) {
          adjustedBoxOfficeReports.push(movieTermWeek.boxOfficeReports?.data)
        } else if (this.isMovieTermWeekChanged(movieTermWeek) || this.isLastMovieTermWeekChanged(movieTermWeek)) {
          adjustedBoxOfficeReports.push(movieTermWeek.boxOfficeReports?.data)
        }
      })

      return _.flatten(adjustedBoxOfficeReports)
    },
    adjustedBoxOfficeReportSettlements () {
      return _(this.adjustedBoxOfficeReports).map(boxOfficeReport => boxOfficeReport.boxOfficeReportSettlements?.data).flatten().value()
    },
    originalMovieTermData () {
      return _.find(this.originalMovieTermsData, { id: this.movieTerm.id })
    },
    dateOfFirstShowing () {
      return this.movieTerm?.movie?.dateOfFirstShowing || '1965-01-01'
    },
    isOnlyDefaultTerm () {
      return this.movieTerm?.isDefault &&
        this.movieTerm?.isRemote &&
        _.filter(this.movieTerm?.movie?.movieTerms?.data, 'isDefault').length === 1
    },
    allMovieTerms () {
      if (_.isNil(this.movieTerm)) return []

      if (this.movieTerm.isRemote) return this.movieTerm.movie.movieTerms.data

      return _.concat(this.movieTerm.movie.movieTerms.data, this.movieTerm)
    },
    lastPreviewMovieTermWeek () {
      return _.last(this.movieTerm.previewMovieTermWeeks)
    },
    lastReleaseMovieTermWeek () {
      return _.last(this.movieTerm.releaseMovieTermWeeks)
    }
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    processSubTableColumns (columns) {
      const rejectColumns = ['movie', 'movieTerm', 'studio']
      if (!this.currentSite.isLibrary) {
        rejectColumns.push('site')
      }

      return _.reject(columns, column => _.includes(rejectColumns, column?.name || column?.field))
    },
    validateMovieTerms () {
      if (_.uniqBy(this.allMovieTerms, 'format').length !== this.allMovieTerms.length) return 'Selected Showing Badge combination must be unique'
    },
    movieTermWeekName (movieTermWeek) {
      if (movieTermWeek.type === 'preview' && movieTermWeek === this.lastPreviewMovieTermWeek) return `${movieTermWeek.name}+`
      if (movieTermWeek.type === 'release' && movieTermWeek === this.lastReleaseMovieTermWeek) return `${movieTermWeek.name}+`

      return movieTermWeek.name
    },
    isMovieTermWeekChanged (movieTermWeek) {
      if (movieTermWeek.isDirty) return true

      const originalMovieTermWeek = this.originalMovieTermWeeks[movieTermWeek.type][movieTermWeek.id]
      if (
        originalMovieTermWeek.termPercentHundredths !== movieTermWeek.termPercentHundredths ||
        originalMovieTermWeek.weekNumber !== movieTermWeek.weekNumber
      ) {
        return true
      }

      return false
    },
    isLastMovieTermWeekChanged (movieTermWeek) {
      const wasLastMovieTermWeek = this.originalMovieTermWeeks[movieTermWeek.type].length === movieTermWeek.weekNumber
      if (!wasLastMovieTermWeek) return false

      const lastMovieTermWeek = movieTermWeek.type === 'preview' ? this.lastPreviewMovieTermWeek : this.lastReleaseMovieTermWeek
      if (lastMovieTermWeek.id !== movieTermWeek.id) return true

      return false
    },
    addMovieTermWeek (type) {
      const lastTermOfType = type === 'preview' ? this.lastPreviewMovieTermWeek : this.lastReleaseMovieTermWeek

      this.movieTerm.movieTermWeeks.add({
        type,
        weekNumber: lastTermOfType?.weekNumber + 1 || 1,
        termPercentHundredths: lastTermOfType?.termPercentHundredths || 0
      })
    },
    deleteMovieTermWeek (term) {
      this.movieTerm.movieTermWeeks.remove(term)

      let weekNumber = 1
      _.each(this.movieTerm.previewMovieTermWeeks, movieTermWeek => {
        movieTermWeek.weekNumber = weekNumber
        weekNumber++
      })
      weekNumber = 1
      _.each(this.movieTerm.releaseMovieTermWeeks, movieTermWeek => {
        movieTermWeek.weekNumber = weekNumber
        weekNumber++
      })
    }
  }
}
</script>
